import { AxiosError } from 'axios'
import React, { FunctionComponent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { matchPath, RouteComponentProps, StaticContext } from 'react-router'

import { MaOrderDetail } from './MaOrderDetail'
import {
  getTrackingInfo,
  isEnrolledProgram,
  isPhysicianAssociated
} from './MaOrderDetail.models'
import {
  AnnounceMode,
  IClinAnnounceBarProps
} from '../../components/ClinAnnounceBar/ClinAnnounceBar'
import { OrderCancellationCodes } from '../../constants'
import { useAppContext } from '../../context/app'
import { createAnnounceEvent } from '../../events/AnnounceEvent'
import { OrderLineCancellationModal } from '../../features/OrderLineCancellationModal'
import { useEffectOnlyOnce } from '../../hooks/useEffectOnlyOnce/useEffectOnlyOnce'
import { AnalyticsEvent } from '../../services/Analytics'
import { AnalyticsPageEvent } from '../../services/Analytics/AnalyticsPageEvent'
import analyticsServiceSingleton from '../../services/Analytics/initAnalytics'
import {
  AuthError,
  cancelGetEnrolledPhysicians,
  cancelGetEnrolledPrograms,
  cancelGetProgramFromSearchIndexById,
  cancelGetReadOnlyOpaUrl,
  getEnrolledPhysicians,
  getEnrolledPrograms,
  getProgramFromSearchIndexById,
  getReadOnlyOpaUrl,
  setDocumentToHold,
  downloadOrderInvoice
} from '../../services/ApiService'
import {
  OrderCancellationSaga,
  createOrderCancellationLogger,
  logOrderCancellationResult
} from '../../services/Saga'
import {
  getHoldDocumentForHold,
  useOrderLevelHolds
} from '../../services/Holds/holds'
import { getHoldForType, HoldType } from '../../services/Holds/holds.constants'
import { IHoldDetails } from '../../services/Holds/holds.types'
import { IError } from '../../types'
import { findOrderCancellationLookupCode } from '../../types/IOrder'
import { IRestEndpointState } from '../../types/IRestEndpointState'
import {
  DeleteOrderDto,
  HoldDto,
  InstituteDto,
  OrderDto,
  OrderLineDto,
  PhysiciansEnrolledSummaryDto,
  ProgramCatalogDto,
  UploadedDocumentDto
} from '../../types/swaggerTypes'
import { useErrorMessage } from '../../utils/useErrorMessage'
import {
  getUpdatedOrderState,
  getUpdatedOrderStateAfterDelete
} from '../OrderPages/OrderDetail/OrderDetail.model'
import { defaultEnrolledSearchParams } from '../Patients/CreatePatient/CreatePatient.model'
import useChangeBackgroundColor from '../../hooks/useChangeBackgroundColor/useChangeBackgroundColor'
import { ClinTheme } from '../../ClinTheme'
import { isOrderEligibleForInvoice } from '../../utils/orderUtils'
import { OPAOrderTypes } from '../Patients/PatientDashboard/Patient.model'

type LocationState = {
  endpoint?: string
  from: string
  orderDto?: OrderDto
  stockOrder?: boolean
  patientId?: number
}

export interface IMaOrderDetailProps
  extends RouteComponentProps<{}, StaticContext, LocationState> {}

export const MaOrderDetailContainer: FunctionComponent<IMaOrderDetailProps> = ({
  history,
  location
}) => {
  const { t } = useTranslation()
  const { orderDto, stockOrder } = location.state
  const matchPatientDetails =
    history.location.state &&
    matchPath(history.location.state.from, {
      path: `/programs/my-physicians/:physicianId/:patientId`
    })
  const isStockOrder = stockOrder || false
  const [isProgramLoading, setIsProgramLoading] = useState<boolean>(true)
  const [isPhysiciansLoading, setIsPhysiciansLoading] = useState<boolean>(true)
  const [isLoadingPatientAccessForm, setIsLoadingPatientAccessForm] =
    useState(true)
  const [order, setOrder] = useState<OrderDto | undefined>(orderDto)
  const [program, setProgram] = useState<ProgramCatalogDto>()
  const [physicians, setPhysicians] = useState<PhysiciansEnrolledSummaryDto[]>()
  const [patientAccessFormUrl, setPatientAccessFormUrl] = useState<
    string | undefined
  >()
  const [isCancelOrderlineModalOpen, setIsCancelOrderlineModalOpen] =
    useState<boolean>(false)
  const [deleteOrderDescription, setDeleteOrderDescription] = useState<
    DeleteOrderDto | undefined
  >()
  const [cancellationSubmitting, setCancellationSubmitting] =
    useState<boolean>(false)
  const { institute, supportContact, user, dispatch } = useAppContext()
  const cancellationOptions =
    OrderCancellationCodes && Object.values(OrderCancellationCodes)
  const [cancellationHasError, setCancellationHasError] = useState<IError>()
  const [isLoadingCancelOrder, setIsLoadingCancelOrder] =
    useState<boolean>(false)

  const [holdDetails, setHoldDetails] = useState<IHoldDetails[]>([])
  const orderLevelHolds = useOrderLevelHolds(undefined, order, supportContact)
  const [enrolledPrograms, setEnrolledPrograms] = useState<string[]>([])

  const [orderAnnounce, setOrderAnnounce] = React.useState<
    IClinAnnounceBarProps | undefined
  >()

  const [isDownloadingInvoice, setIsDownloadingInvoice] =
    useState<boolean>(false)

  const handleAccessFormUrlError = useErrorMessage(
    `There was an error fetching patient access form URL.`
  )
  const handleProgramError = useErrorMessage(
    `There was an error fetching program.`
  )
  const handleEnrolledPhysiciansError = useErrorMessage(
    `There was an error fetching the enrolled physicians.`
  )
  const programId = order?.lines[0].programId?.toString()
  const treatingPhysicianId = order?.lines[0].physician?.physicianId
  const isPhysicianAssociatedToUser: boolean = isPhysicianAssociated(
    treatingPhysicianId,
    physicians
  )
  const isEnrolledProgramToUser: boolean = isEnrolledProgram(
    enrolledPrograms,
    programId
  )

  useChangeBackgroundColor(ClinTheme.colors.lightGrey)

  useEffect(() => {
    if (orderLevelHolds.length) {
      setHoldDetails([getHoldForType(HoldType.CustomDelivery)])
    }
  }, [orderLevelHolds])

  useEffect(() => {
    programId &&
      getProgramFromSearchIndexById(programId)
        .then((result) => {
          setProgram(result.data)
        })
        .catch((error) => handleProgramError(error))
        .finally(() => setIsProgramLoading(false))
    return () => {
      cancelGetProgramFromSearchIndexById()
    }
  }, [handleProgramError, programId])

  useEffect(() => {
    // Only make this call if is a physician associated to the user
    if (
      isPhysicianAssociatedToUser &&
      order?.opaAccessToken &&
      order?.orderNumber
    ) {
      getReadOnlyOpaUrl(Number(order?.orderNumber), {
        opaAccessToken: order?.opaAccessToken
      })
        .then((response) => {
          if (response.data.endPoint) {
            setPatientAccessFormUrl(response.data.endPoint)
          }
        })
        .catch((error) => handleAccessFormUrlError(error))
        .finally(() => setIsLoadingPatientAccessForm(false))
    } else if (order) {
      setIsLoadingPatientAccessForm(false)
    }
    return () => {
      cancelGetReadOnlyOpaUrl()
    }
  }, [handleAccessFormUrlError, order, isPhysicianAssociatedToUser])

  useEffect(() => {
    getEnrolledPrograms(defaultEnrolledSearchParams)
      .then((response) => {
        if (response) {
          setEnrolledPrograms(
            response.data.result.map((program) => program.programId.toString())
          )
        }
      })
      .catch((error: AxiosError) => {
        const { code, message } = error

        if (error.message === AuthError.RequestCancelled) {
          return
        }
        if (error.response) {
          const { title } = error.response.data
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `There was an error fetching enrolled access programs. ${
                title ? title : ''
              } ${message ? message : ''}`
            )
          )
        } else {
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `There was an error fetching enrolled access programs. ${message} ${
                code ? code : ''
              }`
            )
          )
        }

        return () => {
          cancelGetEnrolledPrograms()
        }
      })
  }, [programId, dispatch])

  useEffect(() => {
    programId &&
      getEnrolledPhysicians(programId)
        .then((response) => {
          response && setPhysicians(response.data.physicians)
        })
        .catch((error) => handleEnrolledPhysiciansError(error))
        .finally(() => {
          setIsPhysiciansLoading(false)
        })

    return () => {
      cancelGetEnrolledPhysicians()
    }
  }, [handleEnrolledPhysiciansError, programId])
  type Deps = [OrderDto, IRestEndpointState<InstituteDto>, string]
  useEffectOnlyOnce(
    (dependencies: Deps) => {
      analyticsServiceSingleton.trackPageView(AnalyticsPageEvent.ViewOrder, {
        'Order number': dependencies[0].orderNumber,
        Institution: dependencies[1].data?.instituteName
      })
    },
    [order, institute],
    (dependencies: Deps) => dependencies[0] && dependencies[1]
  )

  const handlePatientAccessForm = () => {
    const physicianId = order?.lines[0].physician?.physicianId
    programId && patientAccessFormUrl
      ? history.push(
          `/programs/access-programs/${programId}/patient-access-form/${physicianId}`,
          {
            endpoint: patientAccessFormUrl,
            from: window.location.pathname
          }
        )
      : history.push('/about/contact-us')
  }

  const handleOrderResupply = () => {
    const physicianId = order?.lines[0].physician?.physicianId
    const patientNumber = order?.lines[0].patientNumber
    order &&
      analyticsServiceSingleton.trackEvent(AnalyticsEvent.PlaceOrderMAAgain, {
        programId,
        orderId: order.orderNumber
      })
    history.push({
      pathname: `/programs/access-programs/${programId}/patient-access-form/${physicianId}`,
      state: {
        orderType: OPAOrderTypes.Resupply,
        patientNumber: patientNumber,
        from: window.location.pathname
      } as LocationState
    })
  }

  const handleCancelOrder = () => {
    const updateOrderLineDto: DeleteOrderDto = {
      cancelReason: '',
      cancelComments: ''
    }
    setDeleteOrderDescription(updateOrderLineDto)
    setIsCancelOrderlineModalOpen(true)
  }

  const handleCancellationOptionChange = (id: string) => {
    if (deleteOrderDescription) {
      const updateDeleteOrderDesc: DeleteOrderDto = {
        ...deleteOrderDescription,
        cancelReason: id ? findOrderCancellationLookupCode[id] : ''
      }
      setDeleteOrderDescription(updateDeleteOrderDesc)
    }
  }

  const handleCancellationTextChange = (comment: string) => {
    if (deleteOrderDescription) {
      const updateOrderLineDto: DeleteOrderDto = {
        ...deleteOrderDescription,
        cancelComments: comment
      }
      setDeleteOrderDescription(updateOrderLineDto)
    }
  }

  const handleSubmitCancellation = async () => {
    if (order && deleteOrderDescription) {
      setCancellationSubmitting(true)
      setIsLoadingCancelOrder(true)

      try {
        // Extract required data from order
        const orderItem = order.lines.find((x) => x.medicationFlag === 'Y')
        const resolvedPhysicianId =
          orderItem?.physician?.physicianId?.toString()
        const resolvedPatientNumber = orderItem?.patientNumber
        const resolvedProgramId = programId?.toString()
        const resolvedPatientId = history.location?.state?.patientId

        // Resolve shipping address ID from multiple sources
        const resolvedShippingAddressId =
          order.deliverTo?.addressId ||
          institute.data?.shipTo?.find((addr) => addr.primaryFlag === 'Y')
            ?.addressId ||
          ''

        if (
          !resolvedPhysicianId ||
          !resolvedPatientNumber ||
          !resolvedProgramId
        ) {
          const missingFields = []
          if (!resolvedPhysicianId) missingFields.push('physicianId')
          if (!resolvedPatientNumber) missingFields.push('patientNumber')
          if (!resolvedProgramId) missingFields.push('programId')

          console.error(
            `Missing required data for order cancellation: ${missingFields.join(
              ', '
            )}. Order: ${order.orderNumber}`
          )
          throw new Error(
            'Unable to cancel order due to missing required information.'
          )
        }

        // Log warning if shipping address ID is missing
        if (!resolvedShippingAddressId) {
          console.warn(
            `Warning: No shipping address ID found for order ${order.orderNumber}. ` +
              `This may cause deleteOpa to fail. Checked sources: ` +
              `order.deliverTo.addressId=${order.deliverTo?.addressId}, ` +
              `institute primary address=${
                institute.data?.shipTo?.find((addr) => addr.primaryFlag === 'Y')
                  ?.addressId
              }`
          )
        }

        // Create cancellation data using the saga helper
        const cancellationData =
          OrderCancellationSaga.createSimpleOrderCancellation(
            order.orderNumber,
            resolvedPhysicianId,
            resolvedPatientNumber,
            resolvedProgramId,
            resolvedShippingAddressId,
            'InitialOrder', // orderType - may need to be determined from order
            deleteOrderDescription.cancelReason,
            deleteOrderDescription.cancelComments || ''
          )

        // Add patient ID if available
        if (resolvedPatientId) {
          cancellationData.patientId = resolvedPatientId
        }

        // Execute the saga with monitoring utilities
        const transactionId = `ma_order_${order.orderNumber}_${Date.now()}`
        const sagaLogger = createOrderCancellationLogger(transactionId)

        const saga = new OrderCancellationSaga(sagaLogger)
        const result = await saga.cancelOrder(cancellationData)

        // Log the result for monitoring
        logOrderCancellationResult(result)

        if (result.success && result.result) {
          // Check if deleteOpa was successful
          if (!result.result.opaDeleted) {
            console.warn(
              `Order cancellation completed but deleteOpa failed for order ${order.orderNumber}. ` +
                `This may cause cross-system validation issues. Manual cleanup may be required.`
            )
          }

          setIsCancelOrderlineModalOpen(false)
          analyticsServiceSingleton.trackEvent(AnalyticsEvent.CancelOrderMA, {
            orderId: order.orderNumber,
            programId,
            location: 'Ma Order Detail'
          })
          history.push('/orders')
        } else {
          // Log detailed error information to console
          const errorMessage =
            result.error?.message || 'Order cancellation failed'
          console.error(
            `Order cancellation failed for order ${order.orderNumber}:`,
            {
              error: errorMessage,
              orderNumber: order.orderNumber,
              patientNumber: resolvedPatientNumber,
              programId: resolvedProgramId,
              shippingAddressId: resolvedShippingAddressId,
              result: result
            }
          )

          // Show generic error to user
          throw new Error(
            'Order cancellation failed. Please try again or contact support if the issue persists.'
          )
        }
      } catch (error) {
        // If request is cancelled continue
        if (
          error instanceof Error &&
          error.message === AuthError.RequestCancelled
        ) {
          return
        }

        // Log detailed error information to console
        console.error(
          `Order cancellation error for order ${order.orderNumber}:`,
          {
            error: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined,
            orderNumber: order.orderNumber,
            timestamp: new Date().toISOString()
          }
        )

        // Show generic error message to user
        const userErrorMessage =
          error instanceof Error &&
          error.message.includes(
            'Unable to cancel order due to missing required information'
          )
            ? error.message
            : 'There was an error when cancelling your order. Please try again or contact support if the issue persists.'

        setCancellationHasError({
          hasError: true,
          errorMessage: userErrorMessage
        })
        analyticsServiceSingleton.trackError(
          error instanceof Error ? error : new Error(String(error)),
          JSON.parse(localStorage.getItem('current_user') || '{}')
        )
      } finally {
        setCancellationSubmitting(false)
        setIsLoadingCancelOrder(false)
      }
    }
  }

  const handleCancellationClose = () => {
    setIsCancelOrderlineModalOpen(false)
    setDeleteOrderDescription(undefined)
  }

  const handleEmailOrderDetails = () => {
    // Validate order status
    if (!isOrderEligibleForInvoice(order)) {
      setOrderAnnounce({
        message: t('ma_order_detail:invoice_download_error'),
        mode: AnnounceMode.Warning,
        isDismissible: true
      })
      return
    }

    if (!order?.orderNumber) {
      return
    }

    setIsDownloadingInvoice(true)

    downloadOrderInvoice(order.orderNumber)
      .then((response) => {
        if (response.status === 200) {
          // Create a URL for the blob
          const blob = new Blob([response.data], { type: 'application/pdf' })
          const downloadUrl = window.URL.createObjectURL(blob)

          // Create a temporary link and click it to download
          const a = document.createElement('a')
          a.href = downloadUrl
          a.download = `invoice_${order.orderNumber}.pdf`
          document.body.appendChild(a)
          a.click()

          // Clean up
          window.URL.revokeObjectURL(downloadUrl)
          document.body.removeChild(a)

          setOrderAnnounce({
            title: ' ',
            message: t('ma_order_detail:invoice_downloaded'),
            mode: AnnounceMode.Success,
            isDismissible: true
          })

          analyticsServiceSingleton.trackEvent(AnalyticsEvent.DownloadInvoice, {
            orderNumber: order.orderNumber,
            orderStatus: order.orderStatus,
            deliverToContact: order.deliverToContact,
            shipMethod: order.shipMethod,
            shipToContact: order.shipToContact,
            type: 'MA'
          })
        }
      })
      .catch((error: AxiosError) => {
        // Enhanced error handling
        let errorMessage = t('ma_order_detail:invoice_download_error')
        if (error.response?.status === 404) {
          errorMessage = t('ma_order_detail:invoice_download_error')
        } else if (error.response?.status === 403) {
          errorMessage = t('ma_order_detail:invoice_download_error')
        } else if (error.response?.status === 500) {
          errorMessage = t('ma_order_detail:invoice_download_error')
        }

        setOrderAnnounce({
          title: 'Error',
          message: `${errorMessage} ${error}`,
          mode: AnnounceMode.Error,
          isDismissible: true
        })

        analyticsServiceSingleton.trackEvent(
          AnalyticsEvent.DownloadInvoiceError,
          {
            orderNumber: order.orderNumber,
            orderStatus: order.orderStatus,
            deliverToContact: order.deliverToContact,
            shipMethod: order.shipMethod,
            shipToContact: order.shipToContact,
            type: 'MA'
          }
        )
      })
      .finally(() => {
        setIsDownloadingInvoice(false)
      })
  }

  const handleGoBack = () => {
    if (
      (history.location.state && history.location.state.from === '/orders') ||
      matchPatientDetails
    ) {
      history.goBack()
    } else {
      history.push('/orders')
    }
  }

  const handleTrackingLinkClicked = (
    orderLine: OrderLineDto,
    currentLanguage: string
  ) => {
    analyticsServiceSingleton.trackEvent(AnalyticsEvent.DeliveryTracking, {
      carrierName: orderLine.trackingInfo?.freightCarrier,
      instituteId: user?.institute?.instituteId,
      language: currentLanguage,
      location: history.location.pathname
    })
  }

  const handleFileUploadedForOrderAndHold = (
    orderId: string,
    orderLine: OrderLineDto,
    hold: HoldDto,
    document: UploadedDocumentDto
  ) => {
    const holdDocumentType: string | undefined = getHoldDocumentForHold(hold)
    if (hold.holdId && holdDocumentType) {
      setDocumentToHold(
        orderId,
        document.uploadedDocumentId,
        hold,
        holdDocumentType
      )
        .then((response) => {
          // Update order with document details
          if (order) {
            setOrder(
              getUpdatedOrderState(
                order,
                orderLine,
                hold,
                holdDocumentType,
                document,
                response?.data?.documentId
              )
            )
          }
        })
        .catch((error) => {
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `An error occurred uploading your document. ${error}`
            )
          )
          // Trigger refresh to remove document
          window.scrollTo(0, 0)
        })
    } else {
      console.warn(
        'No holdId or holdDocumentType supplied to setDocumentToHold'
      )
    }
  }

  const handleFileDeleteForOrderAndHold = (
    orderLine: OrderLineDto,
    hold: HoldDto,
    documentId?: number
  ) => {
    if (hold.holdId && documentId) {
      if (order) {
        setOrder(
          getUpdatedOrderStateAfterDelete(order, orderLine, hold, documentId)
        )
      }
    } else {
      console.warn(
        'No holdId or documentId is provided when deleting a document.'
      )
    }
  }

  const listOfNewFeatures = undefined //expired features

  return (
    <>
      <OrderLineCancellationModal
        isOpen={isCancelOrderlineModalOpen}
        selectedOption={
          deleteOrderDescription &&
          findOrderCancellationLookupCode[deleteOrderDescription?.cancelReason]
        }
        cancellationOptions={cancellationOptions}
        isSubmitting={cancellationSubmitting}
        handleCommentChange={handleCancellationTextChange}
        handleOptionChange={handleCancellationOptionChange}
        handleSubmit={handleSubmitCancellation}
        handleClose={handleCancellationClose}
        hasError={cancellationHasError && cancellationHasError.hasError}
        errorMessage={cancellationHasError && cancellationHasError.errorMessage}
      />
      <MaOrderDetail
        isLoading={
          isLoadingPatientAccessForm ||
          isProgramLoading ||
          isPhysiciansLoading ||
          isLoadingCancelOrder
        }
        newFeaturesList={listOfNewFeatures}
        order={order}
        isStockOrder={isStockOrder}
        program={program}
        orderLevelHolds={holdDetails}
        patientAccessFormUrl={patientAccessFormUrl}
        isPhysicianAssociated={isPhysicianAssociatedToUser}
        isEnrolledProgram={isEnrolledProgramToUser}
        orderAnnounce={orderAnnounce}
        isDownloadingInvoice={isDownloadingInvoice}
        trackingInfo={order && getTrackingInfo(order)}
        handleDismissAnnounce={() => setOrderAnnounce(undefined)}
        handlePatientAccessForm={handlePatientAccessForm}
        handleCancelOrder={handleCancelOrder}
        handleOrderResupply={handleOrderResupply}
        handleGoBack={handleGoBack}
        handleFileUploadedForOrderAndHold={handleFileUploadedForOrderAndHold}
        handleFileDeleteForOrderAndHold={handleFileDeleteForOrderAndHold}
        handleEmailOrderDetails={handleEmailOrderDetails}
        handleTrackingLinkClicked={handleTrackingLinkClicked}
      />
    </>
  )
}
